import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// date-fns format kept as fallback
import { format } from "date-fns";
import AdminLayout from "./AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  CheckCircle2,
  AlertTriangle,
  Clock,
  XCircle,
  Database,
  RefreshCw,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// Types
interface SyncErrorEntry {
  id: number;
  invoiceId: number | null;
  tramsInvoiceNo: number | null;
  invoiceNumber: string | null;
  memberId: number | null;
  memberDisplayName: string | null;
  tramsAccountId: number | null;
  tramsProfileId: number | null;
  tramsInterfaceId: string | null;
  errorType: string;
  errorSubtype: string | null;
  errorMessage: string;
  rawErrorDetails: Record<string, unknown> | null;
  tramsData: Record<string, unknown> | null;
  aiviaData: Record<string, unknown> | null;
  status: string;
  resolvedAt: string | null;
  resolvedByUserId: number | null;
  resolutionNote: string | null;
  resolutionSource: string | null;
  correlationId: string | null;
  operationId: string | null;
  occurrenceCount: number;
  lastOccurredAt: string;
  tripId: number | null;
  teamId: number | null;
  createdAt: string;
  updatedAt: string;
}

interface SyncErrorDetailResult {
  entry: SyncErrorEntry;
  memberEmail: string | null;
  invoiceInvoiceNumber: string | null;
  invoiceTramsInvoiceNumber: string | null;
  tramsAgentName: string | null;
  tripName: string | null;
}

interface PaginatedSyncErrors {
  items: SyncErrorEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const ERROR_TYPE_LABELS: Record<string, string> = {
  failed_sync: "Failed Sync",
  data_conflict: "Data Conflict",
  missing_data: "Missing Data",
  overwrite_error: "Overwrite Error",
  other: "Other",
};

const STATUS_LABELS: Record<string, string> = {
  unresolved: "Unresolved",
  in_progress: "In Progress",
  resolved: "Resolved",
};

const ERROR_TYPE_COLORS: Record<string, string> = {
  failed_sync: "bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800",
  data_conflict: "bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  missing_data: "bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800",
  overwrite_error: "bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800",
  other: "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
};

const STATUS_COLORS: Record<string, string> = {
  unresolved: "bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800",
  in_progress: "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  resolved: "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800",
};

const dateRanges = [
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
  { label: "All time", value: "all" },
];

function getDateRange(preset: string): { from?: string; to?: string } {
  const now = new Date();
  switch (preset) {
    case "24h":
      return { from: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString() };
    case "7d":
      return { from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString() };
    case "30d":
      return { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString() };
    case "90d":
      return { from: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString() };
    default:
      return {};
  }
}

function formatET(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return format(new Date(dateStr), "MMM d, yyyy h:mm a");
  }
}

export default function SyncErrorLogPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // List state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [dateRange, setDateRange] = useState("30d");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [errorTypeFilter, setErrorTypeFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Detail state
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolutionSource, setResolutionSource] = useState<string>("");

  // Build query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    params.set("sortBy", "createdAt");
    params.set("sortDir", "desc");

    if (statusFilter && statusFilter !== "all") {
      params.set("status", statusFilter);
    }
    if (errorTypeFilter && errorTypeFilter !== "all") {
      params.set("errorType", errorTypeFilter);
    }
    if (searchQuery.trim()) {
      params.set("search", searchQuery.trim());
    }

    const range = getDateRange(dateRange);
    if (range.from) params.set("dateFrom", range.from);
    if (range.to) params.set("dateTo", range.to);

    return params.toString();
  }, [page, pageSize, statusFilter, errorTypeFilter, searchQuery, dateRange]);

  // Fetch list
  const { data, isLoading, error } = useQuery<PaginatedSyncErrors>({
    queryKey: ['/api/admin/sync-error-log', queryParams],
    queryFn: () => apiRequest('GET', `/api/admin/sync-error-log?${queryParams}`),
  });

  // Fetch detail
  const { data: detailData, isLoading: detailLoading } = useQuery<SyncErrorDetailResult>({
    queryKey: ['/api/admin/sync-error-log', String(selectedEntryId)],
    queryFn: () => apiRequest('GET', `/api/admin/sync-error-log/${selectedEntryId}`),
    enabled: selectedEntryId !== null,
  });

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: async (params: { id: number; note: string; source?: string }) => {
      return apiRequest("POST", `/api/admin/sync-error-log/${params.id}/resolve`, {
        resolutionNote: params.note,
        resolutionSource: params.source || undefined,
      });
    },
    onSuccess: (_data, variables) => {
      toast({ title: "Resolved", description: "Error entry marked as resolved." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sync-error-log"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sync-error-log", String(variables.id)] });
      setShowResolveDialog(false);
      setResolutionNote("");
      setResolutionSource("");
      setSelectedEntryId(null);
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to resolve", variant: "destructive" });
    },
  });

  // Status update mutation
  const statusMutation = useMutation({
    mutationFn: async (params: { id: number; status: string }) => {
      return apiRequest("PATCH", `/api/admin/sync-error-log/${params.id}/status`, {
        status: params.status,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sync-error-log"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sync-error-log", String(variables.id)] });
    },
  });

  const handleResolve = useCallback(() => {
    if (!selectedEntryId || !resolutionNote.trim()) return;
    resolveMutation.mutate({
      id: selectedEntryId,
      note: resolutionNote,
      source: resolutionSource || undefined,
    });
  }, [selectedEntryId, resolutionNote, resolutionSource, resolveMutation]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= (data?.totalPages ?? 1)) {
      setPage(newPage);
    }
  };

  return (
    <AdminLayout
      title="Sync Error Log"
      description="Central log of TRAMS ↔ AIVIA sync failures and data conflicts. Diagnose, resolve, and track integration issues."
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filters:</span>
          </div>

          <Select value={dateRange} onValueChange={(v) => { setDateRange(v); setPage(1); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {dateRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="unresolved">Unresolved</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>

          <Select value={errorTypeFilter} onValueChange={(v) => { setErrorTypeFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Error Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="failed_sync">Failed Sync</SelectItem>
              <SelectItem value="data_conflict">Data Conflict</SelectItem>
              <SelectItem value="missing_data">Missing Data</SelectItem>
              <SelectItem value="overwrite_error">Overwrite Error</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative flex-1 min-w-[min(100%,440px)]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by error #, member name, email, member ID or error message..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              className="pl-9"
            />
          </div>
        </div>

        {/* Summary stats */}
        {data && (
          <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
            <span>
              Showing {data.items.length} of {data.total} entries
            </span>
            {statusFilter === "all" && (
              <span className="text-red-600 dark:text-red-400 font-medium">
                {data.items.filter((e) => e.status === "unresolved").length} unresolved on this page
              </span>
            )}
          </div>
        )}

        {/* Table */}
        <div className="rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Loading sync errors...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-16 text-sm text-red-500">
              Failed to load sync error log.
            </div>
          ) : !data?.items.length ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <CheckCircle2 className="h-12 w-12 mb-3 text-green-400" />
              <p className="text-sm font-medium">No sync errors found</p>
              <p className="text-xs mt-1">All clear — no matching entries for the current filters.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 dark:border-gray-700">
                  <TableHead className="w-[70px]">ID</TableHead>
                  <TableHead>Date/Time (ET)</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead className="max-w-[300px]">Error</TableHead>
                  <TableHead className="w-[60px]">Count</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((entry) => (
                  <TableRow
                    key={entry.id}
                    className="border-slate-200 dark:border-gray-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-750"
                    onClick={() => setSelectedEntryId(entry.id)}
                  >
                    <TableCell className="font-mono text-xs text-slate-500">
                      #{entry.id}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {formatET(entry.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={ERROR_TYPE_COLORS[entry.errorType] ?? ERROR_TYPE_COLORS.other}>
                        {ERROR_TYPE_LABELS[entry.errorType] ?? entry.errorType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_COLORS[entry.status] ?? STATUS_COLORS.unresolved}>
                        {STATUS_LABELS[entry.status] ?? entry.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {entry.invoiceNumber ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {entry.memberDisplayName || (entry.memberId ? `User#${entry.memberId}` : "—")}
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate text-xs text-slate-600 dark:text-slate-400">
                      {entry.errorMessage}
                    </TableCell>
                    <TableCell className="text-center">
                      {entry.occurrenceCount > 1 && (
                        <Badge variant="secondary" className="text-xs">
                          ×{entry.occurrenceCount}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setSelectedEntryId(entry.id); }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Page {data.page} of {data.totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= (data.totalPages)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={selectedEntryId !== null && !showResolveDialog} onOpenChange={(open) => { if (!open) setSelectedEntryId(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Sync Error #{selectedEntryId}
            </DialogTitle>
            <DialogDescription>
              Full details and resolution options for this sync error entry.
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : detailData ? (
            <div className="space-y-4 text-sm">
              {/* Status & Type */}
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={STATUS_COLORS[detailData.entry.status]}>
                  {STATUS_LABELS[detailData.entry.status]}
                </Badge>
                <Badge variant="outline" className={ERROR_TYPE_COLORS[detailData.entry.errorType]}>
                  {ERROR_TYPE_LABELS[detailData.entry.errorType]}
                  {detailData.entry.errorSubtype && ` (${detailData.entry.errorSubtype})`}
                </Badge>
                {detailData.entry.occurrenceCount > 1 && (
                  <Badge variant="secondary">
                    {detailData.entry.occurrenceCount} occurrences
                  </Badge>
                )}
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-slate-50 dark:bg-gray-900">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Created (ET)</p>
                  <p className="font-medium">{formatET(detailData.entry.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Last Occurred (ET)</p>
                  <p className="font-medium">{formatET(detailData.entry.lastOccurredAt)}</p>
                </div>
                {detailData.entry.resolvedAt && (
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Resolved (ET)</p>
                    <p className="font-medium">{formatET(detailData.entry.resolvedAt)}</p>
                  </div>
                )}
              </div>

              {/* Invoice Context */}
              <div className="p-3 rounded-md border border-slate-200 dark:border-gray-700">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Invoice Context</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">AIVIA ID:</span> {detailData.entry.invoiceId ?? "—"}</div>
                  <div><span className="text-slate-500">Invoice #:</span> {detailData.invoiceTramsInvoiceNumber ?? "—"}</div>
                  <div><span className="text-slate-500">AIN:</span> {detailData.invoiceInvoiceNumber ?? "—"}</div>
                  <div><span className="text-slate-500">TRAMS Invoice No:</span> {detailData.entry.tramsInvoiceNo ?? "—"}</div>
                  <div><span className="text-slate-500">Trip:</span> {detailData.tripName ?? (detailData.entry.tripId ? `#${detailData.entry.tripId}` : "—")}</div>
                </div>
              </div>

              {/* Member Context */}
              <div className="p-3 rounded-md border border-slate-200 dark:border-gray-700">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Member Context</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Member:</span> {detailData.entry.memberDisplayName ?? "—"}</div>
                  <div><span className="text-slate-500">Member ID:</span> {detailData.entry.memberId ?? "—"}</div>
                </div>
              </div>

              {/* TRAMS Account Context */}
              <div className="p-3 rounded-md border border-slate-200 dark:border-gray-700">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">TRAMS Account</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Profile ID:</span> {detailData.entry.tramsProfileId ?? "—"}</div>
                  <div><span className="text-slate-500">Interface ID:</span> {detailData.entry.tramsInterfaceId ?? "—"}</div>
                  <div><span className="text-slate-500">Agent Name:</span> {detailData.tramsAgentName ?? "—"}</div>
                  <div><span className="text-slate-500">Account ID:</span> {detailData.entry.tramsAccountId ?? "—"}</div>
                </div>
              </div>

              {/* Error Message */}
              <div className="p-3 rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30">
                <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase mb-1">Error Message</p>
                <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{detailData.entry.errorMessage}</p>
              </div>

              {/* Raw Error Details (engineers only) */}
              {detailData.entry.rawErrorDetails && (
                <details className="p-3 rounded-md border border-slate-200 dark:border-gray-700">
                  <summary className="text-xs font-semibold text-slate-500 uppercase cursor-pointer">
                    Raw Error Details (Engineering)
                  </summary>
                  <pre className="mt-2 text-xs overflow-x-auto bg-slate-100 dark:bg-gray-900 p-2 rounded max-h-[200px] overflow-y-auto">
                    {JSON.stringify(detailData.entry.rawErrorDetails, null, 2)}
                  </pre>
                </details>
              )}

              {/* Conflict Data (for data_conflict type) */}
              {detailData.entry.errorType === "data_conflict" && (detailData.entry.tramsData || detailData.entry.aiviaData) && (
                <div className="p-3 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30">
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase mb-2">
                    Data Conflict Comparison
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {detailData.entry.tramsData && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1 flex items-center gap-1">
                          <Database className="h-3 w-3" /> TRAMS Version
                        </p>
                        <pre className="text-xs bg-white dark:bg-gray-900 p-2 rounded overflow-auto max-h-[150px]">
                          {JSON.stringify(detailData.entry.tramsData, null, 2)}
                        </pre>
                      </div>
                    )}
                    {detailData.entry.aiviaData && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1 flex items-center gap-1">
                          <RefreshCw className="h-3 w-3" /> AIVIA Version
                        </p>
                        <pre className="text-xs bg-white dark:bg-gray-900 p-2 rounded overflow-auto max-h-[150px]">
                          {JSON.stringify(detailData.entry.aiviaData, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Correlation / Operation IDs */}
              {(detailData.entry.correlationId || detailData.entry.operationId) && (
                <div className="grid grid-cols-2 gap-2 text-xs p-3 rounded-md bg-slate-50 dark:bg-gray-900">
                  {detailData.entry.correlationId && (
                    <div><span className="text-slate-500">Correlation ID:</span> <span className="font-mono">{detailData.entry.correlationId}</span></div>
                  )}
                  {detailData.entry.operationId && (
                    <div><span className="text-slate-500">Operation ID:</span> <span className="font-mono">{detailData.entry.operationId}</span></div>
                  )}
                </div>
              )}

              {/* Resolution info (if resolved) */}
              {detailData.entry.status === "resolved" && detailData.entry.resolutionNote && (
                <div className="p-3 rounded-md border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/30">
                  <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase mb-1">Resolution</p>
                  <p className="text-slate-800 dark:text-slate-200">{detailData.entry.resolutionNote}</p>
                  {detailData.entry.resolutionSource && (
                    <p className="text-xs text-slate-500 mt-1">
                      Accepted version: <span className="font-medium uppercase">{detailData.entry.resolutionSource}</span>
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              {detailData.entry.status !== "resolved" && (
                <div className="flex items-center gap-2 pt-2 border-t border-slate-200 dark:border-gray-700">
                  {detailData.entry.status === "unresolved" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => statusMutation.mutate({ id: detailData.entry.id, status: "in_progress" })}
                      disabled={statusMutation.isPending}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Mark In Progress
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => setShowResolveDialog(true)}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Resolve
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve Sync Error #{selectedEntryId}</DialogTitle>
            <DialogDescription>
              Add a resolution note and, for data conflicts, choose which version to accept.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Resolution Note <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder="Describe the resolution taken..."
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                rows={3}
                className="mt-1"
              />
            </div>

            {detailData?.entry.errorType === "data_conflict" && (
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Accepted Version
                </label>
                <Select value={resolutionSource} onValueChange={setResolutionSource}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select accepted data source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trams">TRAMS (overwrite AIVIA)</SelectItem>
                    <SelectItem value="aivia">AIVIA (keep current, push to TRAMS)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Choosing a version will log the selection. Actual data push occurs via the sync pipeline.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              disabled={!resolutionNote.trim() || resolveMutation.isPending}
            >
              {resolveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Confirm Resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
